import { useEffect, useRef } from "react";

/**
 * Draws a frame of a background: `elapsed` is the time since the start in ms.
 * Frames must depend on `elapsed` only, so a slow or paused tab catches up.
 */
export type DrawFrame = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
  elapsed: number,
) => void;

/** Time drawn once when the device asks for reduced motion. */
const STILL_FRAME_MS = 6000;

/** Runs `draw` on a full-size canvas at every frame, sharp on high-density screens. */
export const useCanvasAnimation = (draw: DrawFrame) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!canvas || !context) return;
    const isReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    let frame = 0;
    let start: number | undefined;

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      canvas.width = canvas.clientWidth * ratio;
      canvas.height = canvas.clientHeight * ratio;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const render = (time: number) => {
      start ??= time;
      context.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
      draw(
        context,
        canvas.clientWidth,
        canvas.clientHeight,
        isReduced ? STILL_FRAME_MS : time - start,
      );
      if (!isReduced) frame = requestAnimationFrame(render);
    };

    resize();
    window.addEventListener("resize", resize);
    frame = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
    };
  }, [draw]);

  return canvasRef;
};
